import { getLocalName } from 'vet-bones/utils';

import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  MappingIdentifierPart,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { SuggestionType } from 'src/containers/ProjectMapping/types';
import { ProjectResource } from 'src/store/state/projects';
import {
  OWL_CLASS_IRI,
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_LABEL_IRI,
  SCHEMA_ORG_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_RANGE_INCLUDES_IRI,
} from 'src/templates/queries/iris';
import { arrayify } from 'src/utils/jsonld/arrayify';
import { SuggestionWithoutResource } from 'src/utils/jsonld/parseSuggestionResults';
import { GenerateMappingsResponse } from 'src/utils/voicebox/hooks/types';

type PropertyInfo = {
  iri: string;
  label: string;
  type: string[];
  domains: string[];
  ranges: string[];
};

type ClassInfo = {
  iri: string;
  label: string;
};

export const transformGenesisMappingsToSuggestions = (
  response: GenerateMappingsResponse,
  fieldLabelToValueMapByResource: Record<string, Record<string, string>> = {},
  resourceById: Record<string, ProjectResource> = {}
): SuggestionWithoutResource[] => {
  if (!response.mappings.length) {
    return [];
  }

  const suggestions: SuggestionWithoutResource[] = [];

  const propertyByIri = new Map<string, PropertyInfo>();
  const classByIri = new Map<string, ClassInfo>();

  response.model.forEach((item) => {
    const id = typeof item['@id'] === 'string' ? item['@id'] : '';

    if (!id) {
      return;
    }

    const types = arrayify(item['@type']).filter(
      (t): t is string => typeof t === 'string'
    );
    const labelValues = arrayify(item[RDFS_LABEL_IRI]);
    const label =
      labelValues
        .map((labelValue) => {
          if (
            typeof labelValue === 'object' &&
            labelValue &&
            '@value' in labelValue
          ) {
            return labelValue['@value'] as string;
          }
          return undefined;
        })
        .find((label): label is string => Boolean(label)) || getLocalName(id);

    if (
      types.includes(OWL_DATATYPE_PROPERTY_IRI) ||
      types.includes(OWL_OBJECT_PROPERTY_IRI)
    ) {
      const domains = arrayify(item[SCHEMA_ORG_DOMAIN_INCLUDES_IRI])
        .map((d) => {
          if (typeof d === 'object' && d && '@id' in d) {
            const domainId = d['@id'];
            return typeof domainId === 'string' ? domainId : undefined;
          }
          return undefined;
        })
        .filter((d): d is string => d !== undefined);
      const ranges = arrayify(item[SCHEMA_ORG_RANGE_INCLUDES_IRI])
        .map((r) => {
          if (typeof r === 'object' && r && '@id' in r) {
            const rangeId = r['@id'];
            return typeof rangeId === 'string' ? rangeId : undefined;
          }
          return undefined;
        })
        .filter((r): r is string => r !== undefined);

      propertyByIri.set(id, {
        iri: id,
        label,
        type: types,
        domains,
        ranges,
      });
    } else if (types.includes(OWL_CLASS_IRI)) {
      classByIri.set(id, { iri: id, label });
    }
  });

  response.mappings.forEach((mapping) => {
    const {
      table_id: tableId,
      concept: classIri,
      id: idFields,
      label: labelFields,
      columns: columnMappings,
    } = mapping;
    const source = tableId;

    const classInfo = classByIri.get(classIri);
    if (!classInfo) {
      console.warn(`Class not found: ${classIri}`);
      return;
    }

    const resource = resourceById[source];

    const fieldLabelToValueMap = resource
      ? fieldLabelToValueMapByResource[resource.name] || {}
      : {};

    if (idFields.length) {
      const fields: MappingIdentifierPart[] = idFields.map((field) => ({
        type: MappingIdentifierType.FIELD,
        value: fieldLabelToValueMap[field] || field,
      }));

      suggestions.push({
        type: SuggestionType.PRIMARY_IDENTIFIER,
        score: 1.0,
        fields,
        source,
      });
    }

    if (labelFields.length) {
      labelFields.forEach((labelField) => {
        suggestions.push({
          type: SuggestionType.LABEL,
          field: fieldLabelToValueMap[labelField] || labelField,
          score: 1.0,
          source,
        });
      });
    }

    if (columnMappings) {
      Object.entries(columnMappings).forEach(([propertyIri, columns]) => {
        const property = propertyByIri.get(propertyIri);

        if (!property) {
          console.warn(`Property not found in model: ${propertyIri}`);
          return;
        }

        const columnArray = Array.isArray(columns) ? columns : [columns];

        const isObjectProperty = property.type.includes(
          OWL_OBJECT_PROPERTY_IRI
        );

        if (isObjectProperty) {
          const isCurrentClassInDomain = property.domains.includes(classIri);
          const isCurrentClassInRange = property.ranges.includes(classIri);
          const fields: MappingIdentifierPart[] = columnArray.map((column) => ({
            type: MappingIdentifierType.FIELD,
            value: fieldLabelToValueMap[column] || column,
          }));

          if (isCurrentClassInDomain) {
            property.ranges.forEach((rangeIri) => {
              const range = classByIri.get(rangeIri);
              if (!range) {
                return;
              }

              suggestions.push({
                type: SuggestionType.RELATIONSHIP,
                iri: property.iri,
                fields,
                label: property.label,
                propertyType: rangeIri,
                propertyTypeLabel: range.label,
                classIri,
                classLabel: classInfo.label,
                direction: RelationshipDirection.OUTGOING,
                score: 1.0,
                source,
              });
            });
          }

          if (isCurrentClassInRange) {
            property.domains.forEach((domainIri) => {
              const domain = classByIri.get(domainIri);
              if (!domain) {
                return;
              }

              suggestions.push({
                type: SuggestionType.RELATIONSHIP,
                iri: property.iri,
                fields,
                label: property.label,
                propertyType: domainIri,
                propertyTypeLabel: domain.label,
                classIri,
                classLabel: classInfo.label,
                direction: RelationshipDirection.INCOMING,
                score: 1.0,
                source,
              });
            });
          }
        } else {
          const fieldValue =
            fieldLabelToValueMap[columnArray[0]] || columnArray[0];
          property.ranges.forEach((rangeIri) => {
            suggestions.push({
              type: SuggestionType.ATTRIBUTE,
              iri: property.iri,
              field: fieldValue,
              label: property.label,
              propertyType: rangeIri,
              classIri,
              classLabel: classInfo.label,
              score: 1.0,
              source,
            });
          });
        }
      });
    }

    suggestions.push({
      type: SuggestionType.CLASS,
      iri: classIri,
      label: classInfo.label,
      score: 1.0,
      source,
    });
  });

  return suggestions;
};
